import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { includes, defaultTo } from 'lodash-es';
import classnames from 'classnames';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { transformContentForFaq } from 'site-modules/shared/components/faq/utils';
import { Faq } from 'site-modules/shared/components/faq/faq';
import { ArticleCards } from 'site-modules/editorial/components/article-cards/article-cards';
import { Calculators } from 'site-modules/shared/components/editorial/calculators/calculators';
import { CategoryCards } from 'site-modules/shared/components/category-cards/category-cards';
import { ContentList } from 'site-modules/editorial/components/content-list/content-list';
import { ColumnList } from 'site-modules/editorial/components/column-list/column-list';
import { DropdownWidget } from 'site-modules/editorial/components/dropdown-widget/dropdown-widget';
import { MarketPlaceList } from 'site-modules/editorial/components/marketplace-list/marketplace-list';
import { ArticleContent } from 'site-modules/editorial/components/article-content/article-content';
import { ArticlePhoto } from 'site-modules/shared/components/editorial/article-photo/article-photo';
import { EmbeddedYoutubeAsync } from 'site-modules/shared/components/video/embedded-youtube/embedded-youtube-async';
import { InlineArticle } from 'site-modules/editorial/components/inline-article/inline-article';
import { NativeVideoAd } from 'site-modules/shared/components/native-ad/video/video-native-ad';
import { InfographicEntry } from 'site-modules/editorial/components/infographic-entry/infographic-entry';
import { ArticleTable } from 'site-modules/shared/components/editorial/article-table/article-table';
import { AppraiserSellModule } from 'site-modules/shared/components/appraiser-sell/appraiser-sell';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { ShoppingLink } from 'site-modules/shared/components/shopping-link/shopping-link';
import { ArticleReadyForEv } from 'site-modules/editorial/components/article-ready-for-ev/article-ready-for-ev';
import { ArticleTreehousePromotion } from 'site-modules/editorial/components/article-treehouse-promotion/article-treehouse-promotion';
import { EngineTypeQuiz } from 'site-modules/shared/components/engine-type-quiz/engine-type-quiz';
import { VinDecoderEntry } from 'site-modules/shared/components/vin-decoder/vin-decoder-entry/vin-decoder-entry';
import { ExcoVideoViewerBlueBg } from 'site-modules/shared/components/exco-video/exco-video-viewer-blue-bg';

import { entriesTypes } from './entries-types';

import { pagesWithThreeMrects } from './ad-constants';
import './entries-renderer.scss';

function getAdBlock({
  adNameWired = 'native',
  adNameMobile = 'mnative',
  position,
  wiredBreakpoints = {},
  mobileBreakpoints = {},
  customSizes = [],
  noRefresh = true,
  refreshable = false,
  wiredOnly = false,
  mobileOnly = false,
  wrapperClass = '',
}) {
  return (
    <NativeAd
      adNameWired={adNameWired}
      adNameMobile={adNameMobile}
      position={position}
      wiredBreakpoints={wiredBreakpoints}
      mobileBreakpoints={mobileBreakpoints}
      nativeStyle="none"
      dfpCollapse
      customSizes={customSizes}
      noRefresh={noRefresh}
      refreshable={refreshable}
      wiredOnly={wiredOnly}
      mobileOnly={mobileOnly}
      useMinHeight
      showLabel
      wrapperClass={wrapperClass}
    />
  );
}

getAdBlock.propTypes = {
  adNameWired: PropTypes.string,
  adNameMobile: PropTypes.string,
  position: PropTypes.string.isRequired,
  wiredBreakpoints: PropTypes.objectOf(PropTypes.bool),
  mobileBreakpoints: PropTypes.objectOf(PropTypes.bool),
  customSizes: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  noRefresh: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.bool]),
  refreshable: PropTypes.bool,
  mobileOnly: PropTypes.bool,
  wiredOnly: PropTypes.bool,
  wrapperClass: PropTypes.string,
};

const RegularContainer = React.forwardRef(({ className = '', children = null, withRow = false, ...restProps }, ref) => {
  return (
    <div ref={ref} className={classnames(className, { 'mb-2': !className.includes('mb-') })} {...restProps}>
      {withRow ? <Row>{children}</Row> : children}
    </div>
  );
});

RegularContainer.displayName = 'RegularContainer';

RegularContainer.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  withRow: PropTypes.bool,
};

export function renderEntries({
  entriesToRender,
  withWrapper = false,
  slug = null,
  categories = [],
  options = {},
  pathname,
  includeAppraisalSellModuleInEntries = false,
  includeTreehousePromo,
}) {
  const entriesToRenderLength = entriesToRender.length;
  const entriesToRenderLastIndex = entriesToRenderLength - 1;
  const { isMobile, canonical, contentId, pageTitle } = options;
  const entryPath = defaultTo(options.entryPath, []);
  let hasVideo = false;
  let hasExcoVideo = false;
  let applyClearfix = false;
  const excoVideoModule = (
    <ExcoVideoViewerBlueBg
      containerClasses="mx-auto my-2"
      containerStyles={{ maxWidth: '540px', aspectRatio: '16 / 9' }}
    />
  );

  return entriesToRender.reduce((entries, entry, index) => {
    switch (true) {
      case entriesTypes.articleGroup.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <ArticleCards entry={entry} />
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.featureGrid.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <CategoryCards entry={entry} />
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.contentList.test(entry.id): {
        entries.push(
          withWrapper ? (
            <RegularContainer key={entry.id}>
              <ContentList entry={entry} />
            </RegularContainer>
          ) : (
            <ContentList entry={entry} key={entry.id} />
          )
        );
        break;
      }
      case entriesTypes.columnList.test(entry.id): {
        entries.push(
          withWrapper ? (
            <RegularContainer key={entry.id}>
              <ColumnList entry={entry} />
            </RegularContainer>
          ) : (
            <ColumnList entry={entry} key={entry.id} />
          )
        );
        break;
      }
      case entriesTypes.calculators.test(entry.id): {
        entries.push(
          withWrapper ? (
            <RegularContainer key={entry.id}>
              <Calculators />
            </RegularContainer>
          ) : (
            <Calculators key={entry.id} />
          )
        );
        break;
      }
      case entriesTypes.edHighImpact.test(entry.id): {
        entries.push(
          <RegularContainer className="high-impact p-0" key={entry.id}>
            <NativeAd
              adNameWired="edhighimpact"
              adNameMobile="medhighimpact"
              nativeStyle="none"
              position={'1'}
              all
              refreshable={false}
              useMinHeight
            />
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.contentCol.test(entry.id): {
        entries.push(
          <Col xs={12} md={8} key={entry.id} className="pe-md-2">
            {renderEntries({
              entriesToRender: entry.children(),
              withWrapper: false,
              slug: null,
              options,
            })}
          </Col>
        );
        break;
      }
      case entriesTypes.rightRailCol.test(entry.id): {
        const shouldAddThirdMrect = includes(pagesWithThreeMrects, slug);
        const adAboveRightRail = {
          adNameWired: 'mrect',
          position: '1',
          wiredBreakpoints: { md: false, lg: true, xl: true },
          noRefresh: true,
          wiredOnly: true,
        };
        const adUnderRightRail = {
          ...adAboveRightRail,
          position: '2',
        };
        const secondaAdUnderRightRail = {
          ...adAboveRightRail,
          position: '3',
        };
        entries.push(
          <Col xs={12} md={4} key={entry.id} className="d-md-flex flex-md-column right-rail-from-entries-renderer">
            <div className="mb-2">{getAdBlock(adAboveRightRail)}</div>
            {renderEntries({
              entriesToRender: entry.children(),
              withWrapper: false,
              slug: null,
              options,
            })}
            {getAdBlock(adUnderRightRail)}
            {shouldAddThirdMrect && (
              <div className="right-rail-third-mrect d-flex justify-content-center align-items-end">
                {getAdBlock(secondaAdUnderRightRail)}
              </div>
            )}
            <NativeVideoAd
              wrapperClass={classnames('native-video-wrapper', {
                separator: !isMobile,
                'px-1': isMobile,
              })}
              wiredBreakpoints={{
                lg: true,
                xl: true,
              }}
              mobileBreakpoints={{
                md: true,
                sm: true,
                xs: true,
              }}
            />
          </Col>
        );
        break;
      }
      case entriesTypes.externalContent.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id} className="mb-0">
            {renderEntries({
              entriesToRender: entry.children(),
              withWrapper: false,
              slug: null,
              options,
            })}
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.container.test(entry.id): {
        const colsCount = entry.metadata('cols').number();
        entries.push(
          <RegularContainer key={entry.id} withRow={colsCount && colsCount > 0}>
            {entry.children().map(containerEntry =>
              colsCount && colsCount > 0 ? (
                <Col xs={12} md={12 / colsCount} key={containerEntry.id}>
                  {renderEntries({
                    entriesToRender: [containerEntry],
                    withWrapper: false,
                    slug,
                    options,
                  })}
                </Col>
              ) : (
                renderEntries({
                  entriesToRender: [containerEntry],
                  withWrapper: false,
                  slug,
                  options,
                })
              )
            )}
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.tcoDropdowns.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <DropdownWidget widgetType="tco" />
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.reviewsDropdowns.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <DropdownWidget />
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.html.test(entry.id): {
        const content = entry.legacy ? entry.content : entry.metadata('htmlContent').value();
        if (content && content.length > 0) {
          const key = `${entry.id}-${index}`;
          const showExcoAbove = !hasExcoVideo && entriesToRenderLength > 1 && index > 0;
          const showExcoBelow = (!hasExcoVideo && entriesToRenderLength === 1) || index === 0;
          if (showExcoAbove || showExcoBelow) hasExcoVideo = true;
          entries.push(
            <RegularContainer key={key} className={classnames(entry.className, { clearfix: applyClearfix })}>
              {!!entry.title && <div className="heading-4 mb-1">{entry.title}</div>}
              {showExcoAbove && (
                <Fragment>
                  {excoVideoModule}
                  <hr className="mb-2" />
                </Fragment>
              )}
              <ArticleContent content={content} />
              {showExcoBelow && excoVideoModule}
            </RegularContainer>
          );
          applyClearfix = false;
        }
        break;
      }
      case entriesTypes.table.test(entry.id): {
        entries.push(
          <ArticleTable
            className="mb-1"
            key={entry.id}
            shareOptions={{
              contentId,
              canonical,
              entryPath: [...entryPath, entry.id],
              pageTitle,
            }}
            entry={entry}
          />
        );
        break;
      }
      case entriesTypes.marketplaceList.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <MarketPlaceList entry={entry} />
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.faq.test(entry.id): {
        const entryContent = transformContentForFaq(entry);
        entries.push(
          <RegularContainer key={entry.id}>
            <Faq
              {...entryContent}
              generateLearnMoreLinks
              pathname={pathname}
              titleComponent="h2"
              questionComponent="h3"
              coloredLinks
            />
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.photo.test(entry.id): {
        const photos = entry.links();
        if (photos.length >= 1) {
          const key = `${entry.id}-${index}`;
          entries.push(
            <RegularContainer key={key}>
              <ArticlePhoto photos={photos} />
            </RegularContainer>
          );
        }
        break;
      }
      case entriesTypes.video.test(entry.id): {
        const videoId = entry.metadata('youtube-videoid').value();

        if (videoId) {
          const key = `${entry.id}-${index}`;
          const isVertical = entry.metadata('vertical').boolean();
          const aspectRatio = isVertical ? '9:16' : '16:9';
          const creativeId = `${isVertical ? 'vertical' : 'horizontal'}-article-video-${index}`;

          const videoProps = {
            videoId,
            videoTitle: 'inline article video',
            creativeId,
            videoAspectRatio: aspectRatio,
            ...(isVertical
              ? {
                  containerClassName: 'rounded-8',
                  className: 'w-100 h-100',
                  placeholderClassName: 'h-100',
                }
              : {}),
          };

          const videoComponent = (
            <AspectRatioContainer aspectRatio={aspectRatio}>
              <EmbeddedYoutubeAsync {...videoProps} />
            </AspectRatioContainer>
          );

          if (isVertical) {
            applyClearfix = true;
            const videoFloat = entry.metadata('float').value();
            entries.push(
              <div
                key={key}
                data-tracking-parent={creativeId}
                className={classnames('vertical-video w-100', {
                  [videoFloat === 'left' ? 'float-start me-2' : 'float-end ms-2']: !isMobile,
                  'mx-auto': isMobile,
                })}
              >
                {videoComponent}
              </div>
            );
          } else {
            entries.push(
              <RegularContainer key={key} data-tracking-parent={creativeId}>
                {hasVideo ? videoComponent : <ScrollElement id="videos">{videoComponent}</ScrollElement>}
              </RegularContainer>
            );
            hasVideo = true;
          }
        }
        break;
      }
      case entriesTypes.inlineContentAd.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <InlineArticle content={entry.metadata} />
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.adUnitSpot.test(entry.id): {
        // no unique key for ads so index is used
        const key = `ad-block-${index}`;
        entries.push(<RegularContainer key={key}>{getAdBlock({ ...entry.options, isMobile })}</RegularContainer>);
        break;
      }
      case entriesTypes.adUnitMrectRepeatable.test(entry.id): {
        const key = `content-mrect-${index}`;
        entries.push(<RegularContainer key={key}>{getAdBlock({ ...entry.options, isMobile })}</RegularContainer>);
        break;
      }
      case entriesTypes.adUnitMrect.test(entry.id): {
        const key = `content-mrect-${index}`;
        entries.push(
          <RegularContainer className="mb-0" key={key}>
            {entry.component}
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.adUnitVideo.test(entry.id): {
        entries.push(
          <RegularContainer className="sponsoredvideo mb-0" key={'sponsoredvideo'}>
            {entry.component}
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.excoPlayerUnit.test(entry.id): {
        entries.push(<RegularContainer key="exco-player">{entry.component}</RegularContainer>);
        break;
      }
      case entriesTypes.adUnitHighImpact.test(entry.id): {
        entries.push(
          <div className="high-impact clearfix mb-2" key={'high-impact'}>
            <div className="me-0 me-md-2 me-lg-0 me-xl-3 p-0">{entry.component}</div>
          </div>
        );
        break;
      }
      case entriesTypes.infoGraphic.test(entry.id): {
        entries.push(
          <RegularContainer key={`${entry.id}-${index.toString()}`}>
            <InfographicEntry entry={entry} />
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.shoppingLink.test(entry.id): {
        entries.push(
          <ShoppingLink
            key={`${entry.id}-${index.toString()}`}
            to={entry.metadata('url').value()}
            label={entry.metadata('label').value()}
            description={entry.metadata('description').value()}
            iconClassName={entry.metadata('icon').value()}
            className="rounded px-1 px-md-1_5 py-0_75 py-md-1 bg-white mx-md-1 mb-1"
            descriptionClassName="mt-0_25"
            iconContainerClassName="me-0_75 me-md-1_5 size-16 text-blue-50"
            iconStyles={{ height: '48px', width: '48px' }}
            data-tracking-id="link_select_tool_select"
          />
        );
        break;
      }
      case entriesTypes.evQuiz.test(entry.id): {
        entries.push(<EngineTypeQuiz className="mb-2" />);
        break;
      }
      case entriesTypes.vinDecoder.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id} className="mb-0">
            <VinDecoderEntry entry={entry} options={options} />
          </RegularContainer>
        );
        break;
      }
      default:
        break;
    }

    const shouldRenderAppraisalModule = entriesToRenderLastIndex === index;

    if (includeAppraisalSellModuleInEntries) {
      if (shouldRenderAppraisalModule) {
        entries.push(
          <Fragment key="appraiser-sell">
            <hr className="mt-0 mb-2" />
            <MinHeightWrapper componentType="APPRAISER_SELL">
              <AppraiserSellModule className="" isAppraiserTabDefault makeModelEnabled canBeClosed={false} isEmbedded />
            </MinHeightWrapper>
            <hr className="my-1_5" />
          </Fragment>
        );
      }
    }

    if (includeTreehousePromo && !index) {
      entries.push(
        <ArticleTreehousePromotion
          key="treehouse-promotion"
          className="mb-3_5"
          categories={categories}
          slug={slug}
          fallback={<ArticleReadyForEv categories={categories} slug={slug} className="mb-3_5" />}
        />
      );
    }

    return entries;
  }, []);
}
